import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Menu from './Menu';
import styles from './Menu-tw-styles';
import { Button } from '../../Generic/Button';
import i18nMessages from '../../../constants/messages/pt-BR';
import config from '../../../config';
import { getCdnImage } from '../../../utils/ImageManager';
import { ListItem } from '../../Generic/List';
// import ALP_Constants from '../../Brand/ArticleListingPage/constants';
import {
  hideElementsBySelectors,
  restoreContentForScreenReaders,
} from '../../../utils/ariaHiddenHelper';

const normalizeForMatch = value => {
  const text = String(value || '')
    .trim()
    .toLowerCase();
  if (typeof text.normalize === 'function') {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }
  return text;
};

const getMenuIconAriaLabel = menuItem => {
  const menuText = menuItem && menuItem.menuText ? menuItem.menuText : '';
  const normalizedText = normalizeForMatch(menuText);
  const normalizedLink = normalizeForMatch(menuItem && menuItem.menuLink);

  if (
    normalizedText.includes('produto') ||
    normalizedLink.includes('/produtos') ||
    normalizedLink.includes('/colecoes')
  ) {
    return 'Menu: ícone de produtos';
  }
  if (
    normalizedText.includes('ingrediente') ||
    normalizedLink.includes('ingrediente')
  ) {
    return 'Menu: ícone de ingredientes';
  }
  if (
    normalizedText.includes('sobre') ||
    normalizedText.includes('nossa historia') ||
    normalizedLink.includes('historia')
  ) {
    return 'Menu: ícone da nossa história';
  }

  return `Menu: ícone de ${menuText || 'menu'}`;
};

// ✅ Produto-only identifier
const isProdutoMenuItem = menuItem => {
  const menuText = menuItem && menuItem.menuText ? menuItem.menuText : '';
  const normalizedText = normalizeForMatch(menuText);
  const normalizedLink = normalizeForMatch(menuItem && menuItem.menuLink);

  return (
    normalizedText.includes('produto') ||
    normalizedLink.includes('/produtos') ||
    normalizedLink.includes('/colecoes')
  );
};

class NavigationComponent extends React.Component {
  constructor() {
    super();
    this.state = {
      activeIndex: -1,
      clickedIndex: 0,
      activeSlotIndexReset: false,

      // ✅ Produto-only: live announcement text
      srProdutoAnnouncement: '',
    };

    this.hiddenElements = [];

    // ✅ Produto-only: temporarily hidden elements inside Produto dropdown
    this._produtoHiddenNodes = [];
    this._produtoRestoreOnFocusIn = null;

    this._srClearTimer = null;
  }

  componentDidMount() {
    window.addEventListener(i18nMessages.resize, () => {
      this.setState({ activeIndex: -1 });
    });
  }

  componentDidUpdate(nextProps) {
    const { isMenuOpen } = this.props;
    if (nextProps.isMenuOpen !== isMenuOpen) {
      this.updateActiveState();
    }
  }

  componentWillUnmount() {
    window.removeEventListener(i18nMessages.resize, () => {
      this.setState({ activeIndex: -1 });
    });

    // ✅ cleanup Produto focus listener
    if (this._produtoRestoreOnFocusIn) {
      document.removeEventListener('focusin', this._produtoRestoreOnFocusIn, true);
      this._produtoRestoreOnFocusIn = null;
    }

    if (this._srClearTimer) {
      clearTimeout(this._srClearTimer);
      this._srClearTimer = null;
    }
  }

  updateActiveState = () => {
    this.setState({ activeIndex: -1 });
  };

  trapFocusInMenu = () => {
    // Trap tab order inside menu when open
    const menuDialog = document.querySelector('.mobileMenuWrapper');
    if (!menuDialog) return;
    const focusableEls = menuDialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];
    menuDialog.addEventListener('keydown', e => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    });
  };

  setModalAriaHidden = isOpen => {
    // Hide siblings from screen readers when modal/menu is open
    const bodyChildren = Array.from(document.body.children);
    bodyChildren.forEach(el => {
      if (!el.classList.contains('mobileMenuWrapper')) {
        if (isOpen) {
          el.setAttribute('aria-hidden', 'true');
        } else {
          el.removeAttribute('aria-hidden');
        }
      }
    });
  };

  // ✅ Find visible dropdown root for desktop (contains close button + ulMenuList)
  getVisibleDropdownRootEl = () => {
    const root = document.getElementById(i18nMessages.desktopNavClose) || document;
    const dropdowns = Array.from(root.querySelectorAll('.dropdown-menu-dt.menu-dt'));
    return dropdowns.find(el => el && el.offsetParent !== null) || null;
  };

  // ✅ Produto-only: hide dropdown content from SR (but not the close button)
  hideProdutoDropdownContentFromSR = () => {
    const dropdownRoot = this.getVisibleDropdownRootEl();
    if (!dropdownRoot) return;

    // Clear any previous stored nodes
    this._produtoHiddenNodes = [];

    // Close button should remain exposed to SR
    const closeBtn = dropdownRoot.querySelector('.header-dropdown-close');

    // Hide these content nodes so SR won't auto-read list counts / old labels
    const nodesToHide = dropdownRoot.querySelectorAll(
      'ul, .menuItemHeader, .menuSlotWrapper, .sub-menu-wraper'
    );

    nodesToHide.forEach(node => {
      if (!node) return;
      if (closeBtn && node === closeBtn) return;
      if (closeBtn && node.contains(closeBtn)) return; // don't hide parent of close

      const prev = node.getAttribute('aria-hidden');
      this._produtoHiddenNodes.push({ node, prev });
      node.setAttribute('aria-hidden', 'true');
    });
  };

  // ✅ Produto-only: restore dropdown content SR visibility
  restoreProdutoDropdownContentForSR = () => {
    if (!this._produtoHiddenNodes || this._produtoHiddenNodes.length === 0) return;

    this._produtoHiddenNodes.forEach(({ node, prev }) => {
      if (!node) return;
      if (prev === null) node.removeAttribute('aria-hidden');
      else node.setAttribute('aria-hidden', prev);
    });

    this._produtoHiddenNodes = [];
  };

  // ✅ Produto-only: exact required announcement in English
  announceProdutoExpanded = () => {
    const msg = 'Produto submenu expandido. List with 3 submenu items';

    this.setState({ srProdutoAnnouncement: '' }, () => {
      window.requestAnimationFrame(() => {
        this.setState({ srProdutoAnnouncement: msg });

        if (this._srClearTimer) clearTimeout(this._srClearTimer);
        this._srClearTimer = setTimeout(() => {
          this.setState({ srProdutoAnnouncement: '' });
        }, 1200);
      });
    });
  };

  // ✅ Produto-only: after expand, move focus to Close (so SR reads "Fechar menu button")
  focusCloseButton = () => {
    const dropdownRoot = this.getVisibleDropdownRootEl();
    if (!dropdownRoot) return;
    const closeBtn = dropdownRoot.querySelector('.header-dropdown-close');
    if (closeBtn && typeof closeBtn.focus === 'function') {
      closeBtn.focus();
    }
  };

  // ✅ Produto-only: Restore menu content only when user tabs INTO submenu (not on close)
  attachRestoreOnlyWhenFocusMovesIntoMenuContent = () => {
    // Remove existing listener if any
    if (this._produtoRestoreOnFocusIn) {
      document.removeEventListener('focusin', this._produtoRestoreOnFocusIn, true);
      this._produtoRestoreOnFocusIn = null;
    }

    this._produtoRestoreOnFocusIn = e => {
      const dropdownRoot = this.getVisibleDropdownRootEl();
      if (!dropdownRoot) return;

      const closeBtn = dropdownRoot.querySelector('.header-dropdown-close');
      const target = e.target;

      // If focus is on close button, do NOT restore yet
      if (closeBtn && target === closeBtn) return;

      // Restore when focus goes to any other focusable element inside dropdown
      if (dropdownRoot.contains(target)) {
        this.restoreProdutoDropdownContentForSR();
        document.removeEventListener('focusin', this._produtoRestoreOnFocusIn, true);
        this._produtoRestoreOnFocusIn = null;
      }
    };

    // Capture phase so restore happens BEFORE SR announces the focused submenu item
    document.addEventListener('focusin', this._produtoRestoreOnFocusIn, true);
  };

  changeActiveMenu = (index, text) => {
    const { activeIndex } = this.state;

    if (index !== -1) {
      const menuDiv = document.getElementById(i18nMessages.desktopNavClose);
      menuDiv && menuDiv.classList.add(i18nMessages.menuopened);
      // Hide content from screen readers when menu opens (desktop only)
      if (window.innerWidth >= 1024) {
        this.hiddenElements = hideElementsBySelectors([
          '#content',
          '#page-footer',
          '.pantene-menu-bar > div > div > div:first-child',
          '.pantene-menu-bar > div > div > div:last-child',
        ]);
      }
      // Hide siblings for mobile modal
      this.setModalAriaHidden(true);
      this.trapFocusInMenu();
    } else {
      this.setModalAriaHidden(false);
    }

    if (text === i18nMessages.icon) {
      this.setState(prevState => ({
        activeSlotIndexReset: !prevState.activeSlotIndexReset,
      }));
    }
    if (text === i18nMessages.animate) {
      this.onAnimate(index);
    }

    const newActiveIndex = activeIndex === index ? -1 : index;

    this.setState(
      {
        activeIndex: newActiveIndex,
      },
      () => {
        const { menuData } = this.props;
        const openedItem =
          newActiveIndex !== -1 && menuData ? menuData[newActiveIndex] : null;

        // If closing or switching away from Produto → restore immediately
        if (newActiveIndex === -1 || !openedItem || !isProdutoMenuItem(openedItem)) {
          if (this._produtoRestoreOnFocusIn) {
            document.removeEventListener('focusin', this._produtoRestoreOnFocusIn, true);
            this._produtoRestoreOnFocusIn = null;
          }
          this.restoreProdutoDropdownContentForSR();
          return;
        }

        // ✅ Produto opening:
        // 1) Hide dropdown content from SR so it won't auto-read list counts/old labels
        this.hideProdutoDropdownContentFromSR();

        // 2) Speak only your required message
        this.announceProdutoExpanded();

        // 3) Ensure focus is on Close button (SR will read "Fechar menu button")
        //    Do it in next frame to let DOM settle
        window.requestAnimationFrame(() => {
          this.focusCloseButton();
        });

        // 4) Restore submenu SR visibility only when user tabs into submenu content
        this.attachRestoreOnlyWhenFocusMovesIntoMenuContent();
      }
    );
  };

  resetMenu = () => {
    const { menuClose } = this.props;

    // Restore aria-hidden when menu resets
    restoreContentForScreenReaders(this.hiddenElements);
    this.hiddenElements = [];

    // ✅ Produto-only cleanup
    if (this._produtoRestoreOnFocusIn) {
      document.removeEventListener('focusin', this._produtoRestoreOnFocusIn, true);
      this._produtoRestoreOnFocusIn = null;
    }
    this.restoreProdutoDropdownContentForSR();
    this.setState({ srProdutoAnnouncement: '' });

    this.setState({
      activeIndex: -1,
    });
    menuClose ? menuClose() : {};
  };

  onAnimate = index => {
    this.setState({ clickedIndex: index });
  };

  closeMenubar = () => {
    const menuDiv = document.getElementById(i18nMessages.desktopNavClose);
    menuDiv && menuDiv.classList.remove(i18nMessages.menuopened);

    // Restore aria-hidden when menu closes
    restoreContentForScreenReaders(this.hiddenElements);
    this.hiddenElements = [];

    // ✅ Produto-only cleanup
    if (this._produtoRestoreOnFocusIn) {
      document.removeEventListener('focusin', this._produtoRestoreOnFocusIn, true);
      this._produtoRestoreOnFocusIn = null;
    }
    this.restoreProdutoDropdownContentForSR();
    this.setState({ srProdutoAnnouncement: '' });

    this.changeActiveMenu(-1, i18nMessages.closeMenubar);
  };

  loginClickHandler = () => {
    const { menuClose } = this.props;
    menuClose();
    window.location.href = '/pt-br/loginpage';
  };

  logoutClick = () => {
    const { userStore } = this.props;
    if (window.location.pathname === '/pt-br/editprofilepage')
      window.location.href = '/pt-br/loginpage';
    const { logout } = userStore;
    logout();
  };

  render() {
    const { menuData, menuClose, locale, userStore } = this.props;
    const { activeIndex, clickedIndex, activeSlotIndexReset, srProdutoAnnouncement } = this.state;
    const i18nMessagesRuntime = require(`../../../constants/messages/${locale}`);
    const { isLoggedIn, userName } = userStore;
    const profileLink = `/${locale}/editprofilepage`;
    const registrationLink = `/${locale}/createprofilepage`;

    return (
      <div className={classNames(styles.wrapper)}>
        {/* ✅ Produto-only: live region announcement */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden'
          }}
        >
          {srProdutoAnnouncement}
        </div>

        {isLoggedIn && <Button className={styles.loginName}>{userName}</Button>}
        {menuClose && (
          <div>
            <Button 
              className={styles.closeButtonMobile} 
              onClick={menuClose}
              aria-label="Fechar menu"
            />
          </div>
        )}

        <div className={classNames(styles.mobileMenuWrapper)}>
          {!config.registrationDisabled && (
            <>
              {' '}
              {isLoggedIn ? (
                <div className={styles.loginContainer}>
                  <Button
                    className={classNames(
                      styles.loginButton,
                      'event_profile_logout'
                    )}
                    onClick={this.logoutClick}
                    data-action-detail="log out"
                  >
                    {i18nMessagesRuntime.logout}
                  </Button>
                  <Button
                    to={profileLink}
                    className={classNames(styles.registerButton)}
                  >
                    {i18nMessagesRuntime.myprofile}
                  </Button>
                </div>
              ) : (
                <div className={styles.loginContainer}>
                  <Button
                    className={classNames(styles.loginButton)}
                    onClick={this.loginClickHandler}
                  >
                    {i18nMessagesRuntime.login}
                  </Button>
                  <Button
                    to={registrationLink}
                    className={classNames(
                      styles.registerButton,
                      'event_profile_register_view_form'
                    )}
                    data-action-detail="register"
                  >
                    {i18nMessagesRuntime.register}
                  </Button>
                </div>
              )}
            </>
          )}

          <ul className={classNames(styles.listwrapper)} role="presentation">
            {menuData &&
              menuData.map((menuItem, menuInd) => (
                <Menu
                  key={menuInd.toString()}
                  activeIndex={activeIndex}
                  menuItem={menuItem}
                  menuIndex={menuInd}
                  menuCloseFn={menuClose}
                  resetMenu={this.resetMenu}
                  changeActiveMenu={this.changeActiveMenu}
                  activeSlotIndexProp={activeSlotIndexReset}
                  menuArray={menuData}
                />
              ))}
          </ul>

          {/* Main back button positioned after menu items for correct tab order */}
          {menuClose && (
            <Button
              className={styles.leftArrow}
              aria-label="Fechar o Submenu ou voltar"
              tabIndex={0}
              onClick={menuClose}
            >
              {''}
            </Button>
          )}

          <div role="presentation" onClick={this.closeMenubar} />

          <ul
            className={classNames(
              styles.leftMenuWrapper,
              activeIndex !== -1 ? 'bg-lightGrey' : '',
              'iconMenuClass'
            )}
            style={{ '--i': `${clickedIndex}` }}
            aria-label="Navigation icons menu"
            aria-hidden="true"
          >
            {menuData &&
              menuData.map((menuItem, menuInd) => (
                <ListItem key={menuInd}>
                  {menuItem.iconCollection &&
                    menuItem.iconCollection.items.length > 0 &&
                    menuItem.iconCollection.items.map(icon => {
                      if (icon.__typename === 'Image') {
                        return (
                          <Button
                            key={icon.sys.id}
                            aria-label={getMenuIconAriaLabel(menuItem)}
                            aria-expanded={activeIndex === menuInd}
                            className={classNames(
                              styles.iconButton,
                              activeIndex === menuInd || activeIndex === -1
                                ? 'bg-transparent'
                                : '',
                              activeIndex === menuInd && activeIndex !== -1
                                ? 'icon-underline'
                                : ''
                            )}
                            onClick={() =>
                              this.changeActiveMenu(menuInd, i18nMessagesRuntime.icon)
                            }
                            role="button"
                            tabIndex={-1}
                          >
                            <span
                              data-i={menuInd}
                              onClick={() => this.onAnimate(menuInd)}
                              aria-hidden="true"
                            >
                              <img
                                key={icon.sys.id}
                                src={getCdnImage(icon.image.url)}
                                alt=""
                                className={classNames(styles.iconImg, icon.styles)}
                                aria-hidden="true"
                              />
                            </span>
                          </Button>
                        );
                      }
                      return null;
                    })}
                </ListItem>
              ))}
          </ul>
        </div>
      </div>
    );
  }
}

export default NavigationComponent;

NavigationComponent.propTypes = {
  menuData: PropTypes.oneOfType([PropTypes.array]).isRequired,
  menuClose: PropTypes.func,
  isMenuOpen: PropTypes.bool,
  locale: PropTypes.string,
  userStore: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

NavigationComponent.defaultProps = {
  menuClose: () => {},
  isMenuOpen: false,
  locale: 'pt-BR',
};